import { TableView } from '@components/table/types';

import { AttributeFieldType, AttributeType } from 'api/attributes/requests';
import { CompanyUserFavorite } from 'api/favorites/requests';
import {
  BASE_ACTION_TYPE,
  JourneyAction,
  JourneyTemplateType,
  JourneyType,
  JourneyV1,
  PublishedVersion,
  UnpublishedChanges,
  VersionComparison,
  RestoreResult,
} from 'api/types';
import { Workspace } from 'api/workspaces/requests';

import { request } from '../request';

export type DuplicateJourneyData = {
  title: string;
  includeTravellers: boolean;
  includeTriggers: boolean;
};

export type IssueAction = {
  action: BASE_ACTION_TYPE;
  description: string;
  shortId: string;
};

export type PaginatedJourneysResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: JourneyType[];
};

export const loadJourneys = async (queryString: string) => {
  const response = await request.get<PaginatedJourneysResponse | JourneyType[]>(
    `v2/journeys/${queryString}`,
  );
  return response.data;
};

export const createJourney = async (data: { title: string; workspace: string }) => {
  const response = await request.post(`v1/journeys/`, data);
  return response.data;
};

export const loadJourney = async (shortId: string) => {
  const response = await request.get<JourneyV1>(`v1/journeys/${shortId}/`);
  return response.data;
};

export const loadTemplateJourney = async (shortId: string) => {
  const response = await request.get<JourneyV1>(`v1/templates/journeys/${shortId}/`);
  return response.data;
};

export const addJourneyTemplate = async (data: {
  journeyId: JourneyTemplateType['shortId'];
  toWorkspaceId: Workspace['shortId'];
}) => {
  const response = await request.post<{ message: string; journeyShortId: JourneyType['shortId'] }>(
    `v1/templates/add-journey-template/`,
    data,
  );
  return response.data;
};

export const editJourney = async ({
  journeyId,
  ...data
}: {
  journeyId: string;
  title: string;
  description?: string;
}) => {
  const response = await request.patch<JourneyType>(`v1/journeys/${journeyId}/`, data);
  return response.data;
};

export const moveJourney = async (shortId: string, workspaceId: string) => {
  return await request.post(`v1/journeys/${shortId}/move/`, { workspace: workspaceId });
};

export const publishJourney = async (
  shortId: string,
  data?: { changeTravellerJourneyIds?: string[] },
) => {
  return await request.post(`v1/journeys/${shortId}/publish/`, data);
};

export const cancelJourneyChanges = async (shortId: string) => {
  return await request.post(`v1/journeys/${shortId}/cancel-changes/`);
};

export const deleteJourney = async (shortId: string) => {
  const response = await request.delete(`v1/journeys/${shortId}/`);
  return response.data;
};

export const duplicateJourney = async (shortId: string, data: DuplicateJourneyData) => {
  const response = await request.post<JourneyType>(`v1/journeys/${shortId}/duplicate/`, data);
  return response.data;
};

export type JourneyExtraState = {
  addedActionIds: Array<string>;
  customNames: Record<string, string>;
};

export type JourneyTableView = TableView<CompanyUserFavorite> & {
  extraState?: JourneyExtraState;
};

export type JourneySettings = {
  id: string;
  timezone?: string;
  notifyManualActionAssignees?: boolean;
};

export const updateJourneySettings = async (shortId: string, data: Omit<JourneySettings, 'id'>) => {
  const response = await request.patch(`v1/journeys/${shortId}/settings/`, data);
  return response.data;
};

export const loadActionEmailTemplate = async (
  action: 'journey' | 'traveller',
  shortId: string,
): Promise<string> => {
  const response = await request.get<string>(`v1/${action}-actions/${shortId}/preview-email/`);
  return response.data;
};

export const loadActionSmsTemplate = async (
  action: 'journey' | 'traveller',
  shortId: string,
): Promise<string> => {
  const response = await request.get<string>(`v1/${action}-actions/${shortId}/preview-sms/`);
  return response.data;
};

// The live (unsaved) editor body { value, variables }. POSTing it renders the
// current draft instead of the saved template/snapshot, so the preview reflects
// edits without saving (which would close the sidepanel).
export type ActionPreviewLiveContent = {
  value: string;
  variables?: Record<string, unknown>;
};

export const previewActionEmailLive = async (
  action: 'journey' | 'traveller',
  shortId: string,
  content: ActionPreviewLiveContent,
): Promise<string> => {
  const response = await request.post<string>(
    `v1/${action}-actions/${shortId}/preview-email/`,
    content,
  );
  return response.data;
};

export const previewActionSmsLive = async (
  action: 'journey' | 'traveller',
  shortId: string,
  content: ActionPreviewLiveContent,
): Promise<string> => {
  const response = await request.post<string>(
    `v1/${action}-actions/${shortId}/preview-sms/`,
    content,
  );
  return response.data;
};

export type JourneyAttributeData = {
  title: string;
  options?: string;
  attributeType: AttributeFieldType;
  isDecimal?: boolean;
};

export const createAttribute = async (journeyShortId: string, data: JourneyAttributeData) => {
  const response = await request.post<AttributeType>(
    `/v1/journeys/${journeyShortId}/attributes/`,
    data,
  );
  return response.data;
};

export const visibilityMap = {
  disabled: { isActive: false, isRequired: false },
  optional: { isActive: true, isRequired: false },
  required: { isActive: true, isRequired: true },
};

export type AttributeVisibility = keyof typeof visibilityMap;
export type AttributeData = {
  title?: string;
  isActive?: boolean;
  options?: string | null;
  attributeType?: AttributeFieldType;
  isDecimal?: boolean;
};

export const updateAttribute = async ({
  shortId,
  data = {},
}: {
  shortId: string;
  data?: AttributeData;
}) => {
  const response = await request.patch(`v1/journeys/attributes/${shortId}/`, data);
  return response.data;
};

export const removeAttribute = async (shortId: string) => {
  const response = await request.delete(`v1/journeys/attributes/${shortId}/`);
  return response.data;
};

export const updateAttributeOrders = async (shortId: string, data: Record<string, number>) => {
  const response = await request.patch(`v1/journeys/${shortId}/attributes-order/`, {
    shortIds: data,
  });
  return response.data;
};

export const loadJourneyAttributes = async (
  shortId: string,
  params?: Record<string, string | number | boolean>,
) => {
  const response = await request.get<AttributeType[]>(`v1/journeys/${shortId}/attributes/`, {
    params,
  });
  return response.data;
};

export const loadTemplateJourneyAttributes = async (
  shortId: string,
  params?: Record<string, string | number | boolean>,
) => {
  const response = await request.get<AttributeType[]>(
    `v1/templates/journey/${shortId}/attributes/`,
    { params },
  );
  return response.data;
};

export const loadJourneyIssues = async (journeyShortId: string) => {
  const response = await request.get<IssueAction[]>(`v1/journeys/${journeyShortId}/issues/`);
  return response.data;
};

export const updateJourneyCollapse = async (
  shortId: JourneyType['shortId'],
  collapsedActions: Array<JourneyAction['shortId']>,
) => {
  const response = await request.post(`v1/journeys/${shortId}/user-collapse/`, {
    collapsedActions,
  });
  return response.data;
};

export type TimeSavedData = {
  days: number;
  hours: number;
  minutes: number;
};

export const loadJourneyOverview = async (journeyShortId: string) => {
  const response = await request.get<TimeSavedData>(`/v1/journeys/${journeyShortId}/overview/`);
  return response.data;
};

export const loadPublishedVersions = async (journeyShortId: string) => {
  const response = await request.get<{ versions: PublishedVersion[] }>(
    `v1/journeys/${journeyShortId}/published-versions/`,
  );
  return response.data;
};

export const loadPublishedVersionsCount = async (journeyShortId: string) => {
  const response = await request.get<{ count: number }>(
    `v1/journeys/${journeyShortId}/published-versions/count/`,
  );
  return response.data;
};

export const loadUnpublishedChanges = async (journeyShortId: string) => {
  const response = await request.get<UnpublishedChanges>(
    `v1/journeys/${journeyShortId}/unpublished-changes/`,
  );
  return response.data;
};

export const compareWithCurrent = async (journeyShortId: string, versionId: string) => {
  const response = await request.get<VersionComparison>(
    `v1/journeys/${journeyShortId}/compare-with-current/`,
    {
      params: {
        version: versionId,
      },
    },
  );
  return response.data;
};

export const restoreToVersion = async (
  journeyShortId: string,
  versionId: string,
  confirm: boolean = true,
) => {
  const response = await request.post<RestoreResult>(
    `v1/journeys/${journeyShortId}/restore-version/`,
    { publishedJourneyId: versionId, confirm },
  );
  return response.data;
};

export const fetchVersionPreview = async (journeyShortId: string, versionShortId: string) => {
  const response = await request.get<JourneyAction[]>(
    `v1/journeys/${journeyShortId}/versions/${versionShortId}/`,
  );
  return response.data;
};

export const updateVersionName = async (
  journeyShortId: string,
  versionShortId: string,
  name: string | null,
) => {
  const response = await request.patch<PublishedVersion>(
    `v1/journeys/${journeyShortId}/published-versions/${versionShortId}/`,
    { name },
  );
  return response.data;
};
